import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2 } from "lucide-react";
import { INDUSTRIES, SERVICES } from "@/data/content";
import { useSEO } from "@/lib/seo";
import { trackCTA } from "@/lib/analytics";
import CTASection from "@/components/CTASection";

export default function IndustryDetail() {
  const { slug } = useParams();
  const ind = INDUSTRIES.find((i) => i.slug === slug);

  useSEO({
    title: ind ? `${ind.name} Compliance Support` : "Industry",
    description: ind ? `${ind.short} ${ind.body}` : "",
    path: `/industries/${slug}`,
  });

  if (!ind) return <Navigate to="/industries" replace />;
  const Icon = ind.icon;

  return (
    <>
      <section className="relative overflow-hidden bg-navy-900 noise-overlay">
        <div className="absolute inset-0 grid-pattern opacity-40" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8 py-16 lg:py-20">
          <Link to="/industries" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-400 hover:text-white transition-colors" data-testid="back-to-industries">
            <ArrowLeft className="h-4 w-4" aria-hidden="true" /> All Industries
          </Link>
          <div className="mt-6 flex items-center gap-4">
            <span className="flex h-14 w-14 items-center justify-center rounded-xl bg-emerald-500 text-white">
              <Icon className="h-7 w-7" aria-hidden="true" />
            </span>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-amber-500">Industry</p>
          </div>
          <h1 className="mt-6 max-w-3xl font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-white">
            {ind.name} Compliance Support
          </h1>
        </div>
      </section>

      <section className="bg-white py-16 lg:py-20">
        <div className="mx-auto max-w-4xl px-5 lg:px-8">
          <p className="text-lg leading-relaxed text-slate-700">{ind.body}</p>

          <h2 className="mt-12 font-heading text-2xl font-bold text-navy-900">How we support {ind.name.toLowerCase()} facilities</h2>
          <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
            {[
              "Full chemical inventory data collection",
              "SDS review and hazard classification",
              "Maximum & average quantity calculations",
              "State-specific Tier II preparation",
              "Multi-facility coordination",
              "Annual reporting updates and audit prep",
            ].map((d, i) => (
              <li key={i} className="flex items-start gap-3">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" aria-hidden="true" />
                <span className="text-sm leading-relaxed text-slate-700">{d}</span>
              </li>
            ))}
          </ul>

          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/services/tier-ii-reporting-support" className="inline-flex items-center gap-1 text-sm font-semibold text-navy-900 hover:text-amber-600 transition-colors" data-testid="industry-link-tierii">
              Tier II Reporting Support <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>

          <div className="mt-12 rounded-xl border border-slate-200 bg-slate-50 p-8 text-center">
            <h3 className="font-heading text-xl font-bold text-navy-900">Ready to reduce your reporting workload?</h3>
            <Link
              to="/free-tier-ii-assessment"
              onClick={() => trackCTA("Assessment", `industry-${slug}`)}
              className="mt-5 inline-flex items-center gap-2 rounded-md bg-amber-500 px-6 py-3 text-sm font-bold text-navy-900 transition-transform hover:-translate-y-0.5 hover:bg-amber-400"
              data-testid="industry-cta-assessment"
            >
              Get a Free Tier II Assessment <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>
      <CTASection location={`industry-${slug}-footer`} />
    </>
  );
}
